import {
  Card,
  CardContent,
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography
} from '@material-ui/core';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { SRLWrapper, useLightbox } from 'simple-react-lightbox';

// local imports
import { mapActivationStatus } from 'app/utils/mapper';
import { AsyncButton, CustomBadge } from 'app/components/atoms';
import { CampaignService } from 'app/services';
import { useStateCallback } from 'app/hooks';
import { useCallback } from 'react';

// styles
const useStyles = makeStyles(theme => ({
  avatarWrapper: {
    position: 'relative',
    overflow: 'none',
    display: 'block',
    '&:hover': {
      '& $showBtn': {
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        display: 'flex'
      }
    }
  },
  avatar: {
    border: `4px solid ${theme.palette.background.default}`,
    objectFit: 'cover',
    position: 'absolute'
  },
  showBtn: {
    display: 'none',
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: '0',
    left: '0',
    zIndex: 5
  }
}));

// animation variants
const photo = {
  show: { opacity: 1, x: 0, scale: 1, transition: { delay: 0.2 } },
  hidden: { scale: 0, opacity: 0 }
};
const text = {
  hidden: { opacity: 0, x: -40 },
  show: { opacity: 1, x: 0, transition: { delay: 0.2 } }
};

// ligthbox options
const options = {
  buttons: {
    showAutoplayButton: false,
    showDownloadButton: false,
    showThumbnailsButton: false,
    showNextButton: false,
    showPrevButton: false,
    showFullscreenButton: true,
    showCloseButton: true
  },
  thumbnails: {
    showThumbnails: false
  },
  settings: {
    disablePanzoom: true
  }
};

const CampaignCard = ({ handleRefresh, service }) => {
  const classes = useStyles();
  const { openLightbox } = useLightbox();
  const [loading, setLoading] = useStateCallback();

  const campaignStatus = service?.status?.id === 1 ? 2 : 1;

  // handle update campaign status
  const handleUpdateStatus = useCallback(
    async campaignId => {
      setLoading(true, async () => {
        const serviceFn = () =>
          CampaignService.updateCampaignStatus(campaignId, { status: campaignStatus });
        try {
          await serviceFn();
          // re-fetch
          handleRefresh();
        } catch (err) {
          console.log('Error -> ', err);
        } finally {
          setLoading(false);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setLoading, campaignStatus]
  );

  // variables
  const btnColor = service?.status?.id === 2 ? 'secondary' : 'primary';
  const btnText = service?.status?.id === 2 ? 'Aktifleştir' : 'Pasifleştir';
  const apiURL = process.env.REACT_APP_API;
  const campaignPhotoSrc = service?.content_url ? `${apiURL}/${service?.content_url}` : 'A';
  const statusBadge = mapActivationStatus(service?.status?.id);
  const elements = [
    { src: campaignPhotoSrc, caption: 'Kampanya Görseli', width: 'auto', height: 'auto' }
  ];

  return (
    <>
      <Grid container spacing={2} className="mb-16">
        <Grid item xs={12}>
          <Card elevation={4}>
            {/* Campaign Photo */}
            <CardContent className="flex items-start">
              <motion.div
                className={clsx(classes.avatarWrapper, 'w-96 h-96')}
                variants={photo}
                initial="hidden"
                animate="show">
                <img
                  alt="campaign content"
                  className={clsx(classes.avatar, 'w-full h-full rounded-12 bg-gray-300')}
                  src={campaignPhotoSrc}
                />

                {/* Show Campaign Photo */}
                {service?.content_url && (
                  <>
                    <Tooltip title="Fotoğrafı Göster" placement="top">
                      <div className={clsx(classes.showBtn, 'justify-center items-center')}>
                        <IconButton
                          aria-label="show_photo"
                          size="medium"
                          onClick={() => openLightbox()}>
                          <VisibilityIcon fontSize="large" />
                        </IconButton>
                      </div>
                    </Tooltip>
                  </>
                )}
              </motion.div>

              <SRLWrapper options={options} elements={elements} />

              {/* Campaign Info -> Name-Status */}
              <motion.div
                className="flex-1 flex flex-col justify-start items-start p-8 mx-12 space-y-6"
                variants={text}
                initial="hidden"
                animate="show">
                <Typography
                  className="text-14 md:text-16 font-semibold text-grey-800 break-all"
                  variant="h5">
                  {service?.name}
                </Typography>
                <Typography variant="subtitle1">
                  <span className="text-14 font-semibold text-gray-800">Kampanya Kodu: </span>
                  <span className="text-gray-700 text-12">{service?.campaign_code}</span>
                </Typography>
                <CustomBadge content={statusBadge.text} status={statusBadge.color} />
              </motion.div>

              <motion.div variants={text} initial="hidden" animate="show">
                <AsyncButton
                  type="button"
                  className=""
                  color={btnColor}
                  text={btnText}
                  loading={loading}
                  handleClick={() => handleUpdateStatus(service?.id)}
                />
              </motion.div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default CampaignCard;
